import { 
  ILoyaltyDataProvider, 
  ITierCalculator, 
  LoyaltyData, 
  LoyaltyStats, 
  TierProgress, 
  TierLevel, 
  TIER_THRESHOLDS 
} from './types';


export class TierCalculator implements ITierCalculator {
  calculateProgress(points: number): TierProgress {
    const currentTier = this.getTierName(points);
    const nextTier = this.getNextTier(currentTier);
    
    if (!nextTier) {
      return {
        current: currentTier,
        next: null,
        pointsRequired: 0,
        pointsEarned: points,
        progressPercentage: 100
      };
    }

    const currentThreshold = TIER_THRESHOLDS[currentTier as TierLevel];
    const nextThreshold = TIER_THRESHOLDS[nextTier];
    const pointsInCurrentTier = points - currentThreshold;
    const pointsRequiredForNextTier = nextThreshold - currentThreshold;
    const progressPercentage = Math.min(100, (pointsInCurrentTier / pointsRequiredForNextTier) * 100);

    return {
      current: currentTier,
      next: nextTier,
      pointsRequired: nextThreshold - points,
      pointsEarned: points,
      progressPercentage: Math.round(progressPercentage)
    };
  }

  getNextTierRequirements(currentTier: string): number {
    const nextTier = this.getNextTier(currentTier);
    return nextTier ? TIER_THRESHOLDS[nextTier] : 0;
  }

  getTierName(points: number): string {
    const tiers = Object.entries(TIER_THRESHOLDS).reverse();
    
    for (const [tier, threshold] of tiers) {
      if (points >= threshold) {
        return tier;
      }
    }
    
    return TierLevel.IRON;
  }

  private getNextTier(currentTier: string): TierLevel | null {
    const tierOrder = [TierLevel.IRON, TierLevel.BRONZE, TierLevel.SILVER, TierLevel.GOLD, TierLevel.PLATINUM];
    const currentIndex = tierOrder.indexOf(currentTier as TierLevel);
    
    if (currentIndex === -1 || currentIndex === tierOrder.length - 1) {
      return null;
    }
    
    return tierOrder[currentIndex + 1];
  }
}

export class LoyaltyService {
  private tierCalculator: ITierCalculator;

  constructor(tierCalculator: ITierCalculator = new TierCalculator()) {
    this.tierCalculator = tierCalculator;
  }

  /**
   * Calculate loyalty statistics based on current points
   */
  calculateLoyaltyStats(points: number): LoyaltyStats {
    const progress = this.tierCalculator.calculateProgress(points);
    
    return {
      totalPoints: points,
      currentTier: progress.current,
      nextTier: progress.next,
      pointsToNextTier: progress.pointsRequired,
      progress
    };
  }

  /**
   * Get user-friendly tier display name
   */
  getTierDisplayName(tier: string): string {
    if (tier === TierLevel.IRON) {
      return 'Just Started';
    }
    return tier;
  }

  /**
   * Calculate overall getting started progress
   */
  calculateGettingStartedProgress(completedCount: number, totalCount: number): number {
    if (totalCount === 0) return 0;
    return Math.round((completedCount / totalCount) * 100);
  }

  /**
   * Get tier color for UI styling
   */
  getTierColor(tier: string): string {
    const colors = {
      [TierLevel.IRON]: '#6B7280',
      [TierLevel.BRONZE]: '#D97706',
      [TierLevel.SILVER]: '#6B7280',
      [TierLevel.GOLD]: '#F59E0B',
      [TierLevel.PLATINUM]: '#8B5CF6'
    };
    
    return colors[tier as TierLevel] || colors[TierLevel.IRON];
  }

  /**
   * Get estimated time to next tier based on average activity
   */
  getEstimatedTimeToNextTier(pointsNeeded: number, averagePointsPerWeek: number = 10): string {
    if (pointsNeeded <= 0) return 'Max tier reached';
    
    const weeksNeeded = Math.ceil(pointsNeeded / averagePointsPerWeek);
    
    if (weeksNeeded === 1) return '1 week';
    if (weeksNeeded < 4) return `${weeksNeeded} weeks`;
    if (weeksNeeded < 12) return `${Math.ceil(weeksNeeded / 4)} months`;
    
    return `${Math.ceil(weeksNeeded / 52)} years`;
  }
}
